import React from 'react';

import type { IResumo } from '../../../../../../hooks/useFechamentoContabilMensal';
import {
  aparenciaDoParecer,
  brl,
  brlCurto,
  mesCurto,
  mesLongo,
  pct,
  SEVERIDADE,
  variacao,
} from '../../regras';
import { sobreAReceita } from '../../regrasDaCarteira';
import { CAIXA, delta, pilula, SUB, TITULO } from './estilos';

const KPI: React.CSSProperties = {
  minWidth: 0,
  padding: '14px 16px',
  borderRadius: 12,
  border: '1px solid #E4E4E7',
  background: 'rgba(255, 255, 255, .6)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const fmt1 = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Variação percentual com seta, contra o mês anterior. */
function Variacao({ atual, anterior }: { atual?: number | null; anterior?: number | null }) {
  const v = variacao(atual, anterior);
  if (v == null) return null;
  return (
    <b style={{ color: v >= 0 ? '#15803D' : '#B91C1C' }}>
      {v >= 0 ? '▲' : '▼'} {pct(Math.abs(v), 0)}
    </b>
  );
}

function Kpi({
  rotulo,
  valor,
  cor = '#0F172A',
  tam = 22,
  onClick,
  children,
}: {
  rotulo: string;
  valor: React.ReactNode;
  cor?: string;
  tam?: number;
  /** com clique, o número abre o detalhe dele */
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const Caixa = onClick ? 'button' : 'div';
  return (
    <Caixa
      {...(onClick ? { type: 'button' as const, onClick, className: 'ind' } : {})}
      style={
        onClick
          ? { ...KPI, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: '#0F172A' }
          : KPI
      }
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: '#64748B',
        }}
      >
        {rotulo}
      </span>
      <span style={{ fontSize: tam, fontWeight: 700, lineHeight: 1.15, color: cor }}>{valor}</span>
      <span style={{ fontSize: 11.5, color: '#52525B' }}>{children}</span>
    </Caixa>
  );
}

/** Parecer da revisão: a cor e o texto do sistema, com a justificativa. */
export function ParecerDaRevisao({ resumo }: { resumo: IResumo }) {
  const ap = aparenciaDoParecer(resumo.parecer.status);
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px 28px',
        padding: '14px 18px',
        borderRadius: 12,
        border: '1px solid #E4E4E7',
        borderLeft: `4px solid ${ap.cor}`,
        background: 'rgba(255, 255, 255, .6)',
      }}
    >
      <div style={{ minWidth: 160 }}>
        <div style={{ fontSize: 11, color: '#64748B' }}>Parecer</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: ap.cor }}>{ap.rotulo}</div>
        <div style={{ fontSize: 11, color: '#64748B' }}>Calculado pelo sistema</div>
      </div>
      <div
        style={{
          flex: '1 1 360px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 12.5,
          color: '#3F3F46',
        }}
      >
        <span style={{ fontSize: 11.5, color: '#64748B' }}>{ap.explicacao}</span>
        <span>{resumo.parecer.justificativa}</span>
      </div>
    </div>
  );
}

/** Anel de progresso: quantos de quantos, como no layout. */
function Anel({
  titulo,
  sub,
  valor,
  total,
  cor,
  rotulo,
}: {
  titulo: string;
  sub: string;
  valor: number;
  total: number;
  cor: string;
  rotulo: string;
}) {
  const fracao = total ? Math.min(1, Math.max(0, valor / total)) : 0;
  const texto = `${valor}/${total}`;
  return (
    <section aria-label={titulo} style={{ ...CAIXA, gap: 10 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={TITULO}>{titulo}</div>
        <span style={SUB}>{sub}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <svg width="76" height="76" viewBox="0 0 36 36" role="img" aria-label={texto}>
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E2E8F0" strokeWidth="3.6" />
          {fracao > 0 && (
            <circle
              cx="18"
              cy="18"
              r="15.9"
              fill="none"
              stroke={cor}
              strokeWidth="3.6"
              strokeLinecap="round"
              strokeDasharray={`${(fracao * 99.9).toFixed(1)} 99.9`}
              transform="rotate(-90 18 18)"
            />
          )}
          <text
            x="18"
            y="20.5"
            textAnchor="middle"
            fontSize="7.2"
            fontWeight="700"
            fill="#0F172A"
            fontFamily="Inter, system-ui, sans-serif"
          >
            {texto}
          </text>
        </svg>
        <span style={{ fontSize: 12.5, color: '#52525B' }}>{rotulo}</span>
      </div>
    </section>
  );
}

const CORES_DO_DESTINO = [
  '#93C5FD',
  '#60A5FA',
  '#3B82F6',
  '#1B6FD0',
  '#155FB0',
  '#0F4C92',
  '#16A34A',
];

/**
 * Aba Resumo do detalhe: parecer, números do mês, operação contra o mês anterior,
 * receita e resultado por mês, destino da receita e os principais pontos. Tudo
 * vem da revisão gravada; o mês anterior vem da revisão gravada dele.
 */
export function AbaResumo({
  resumo,
  anterior,
  competencia,
  onVerTodas,
  completo = false,
  onCarga,
}: {
  resumo: IResumo;
  /** resumo da competência anterior, quando ela foi revisada */
  anterior: IResumo | undefined;
  competencia: string;
  onVerTodas: () => void;
  /** tela completa da empresa: todos os pontos, os anéis e as limitações */
  completo?: boolean;
  /** clique na carga tributária: abre os tributos do mês */
  onCarga?: () => void;
}) {
  const { serie } = resumo;
  const atualDaSerie = serie[serie.length - 1];
  const antDaSerie = serie.length > 1 ? serie[serie.length - 2] : undefined;
  const ind = resumo.indicadores_chave;
  const { mes } = resumo.dre;
  const mesAnt = anterior?.dre.mes;
  const menores = resumo.contagem.media + resumo.contagem.baixa + resumo.contagem.informativa;
  const compAnt = (() => {
    const [a, m] = competencia.split('-').map(Number);
    return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
  })();

  // ---- resumo da operação: receita líquida e margens contra o mês anterior ----
  const rl = mes.receita_liquida;
  const rlAnt = mesAnt?.receita_liquida;
  const margem = (chave: string, dre?: Record<string, number>) =>
    dre ? sobreAReceita(dre[chave], dre.receita_liquida) : null;
  const ppDe = (chave: string) => {
    const a = margem(chave, mes);
    const b = margem(chave, mesAnt);
    return a == null || b == null ? null : (a - b) * 100;
  };
  const antTexto = (v: string) => `${mesLongo(compAnt)}: ${mesAnt ? v : 'sem revisão'}`;
  const distorcida = ind.margem_liquida_mes?.distorcido_por ?? [];
  const varRl = variacao(rl, rlAnt);
  const operacao = [
    {
      rotulo: 'Receita líquida',
      valor: brl(rl),
      d: delta(varRl == null ? null : varRl * 100, `${fmt1(Math.abs((varRl ?? 0) * 100))}%`),
      sub: antTexto(brl(rlAnt)),
    },
    ...(
      [
        ['Margem bruta', 'lucro_bruto'],
        ['Margem operacional', 'resultado_operacional'],
        ['Margem líquida', 'lucro_liquido'],
      ] as const
    ).map(([rotulo, chave]) => {
      const pp = ppDe(chave);
      const extra =
        chave === 'lucro_liquido' && distorcida.length
          ? ` · distorcida por ${distorcida.slice(0, 2).join(' e ')}`
          : '';
      return {
        rotulo,
        valor: pct(margem(chave, mes)),
        d: delta(pp, `${fmt1(Math.abs(pp ?? 0))} p.p.`),
        sub: antTexto(pct(margem(chave, mesAnt))) + extra,
      };
    }),
    {
      rotulo: 'EBITDA',
      valor: pct(margem('ebitda', mes)),
      d: delta(ppDe('ebitda'), `${fmt1(Math.abs(ppDe('ebitda') ?? 0))} p.p.`),
      sub: `${brlCurto(mes.ebitda)} no mês · ${antTexto(pct(margem('ebitda', mesAnt)))}`,
    },
  ];

  // ---- gráfico: barras = receita bruta por mês; linha = lucro líquido ----
  const receitas = serie.map(s => s.receita_bruta ?? 0);
  const lucros = serie.map(s => s.lucro_liquido ?? 0);
  const max = Math.max(1, ...receitas) * 1.12;
  const n = Math.max(1, serie.length);
  const larg = (100 - 4 - 4 * (n - 1)) / n;
  const cx = (i: number) => 2 + i * (larg + 4) + larg / 2;
  const yL = (v: number) => Math.min(100, Math.max(0, 100 - (v / max) * 100));

  // ---- para onde foi a receita ----
  const receita = mes.receita_bruta || 0;
  const destino = [
    ['Deduções e impostos', -mes.deducoes],
    ['Custo das vendas', -mes.custos],
    ['Pessoal', -mes.despesas_pessoal],
    ['Despesas operacionais', -((mes.despesas_operacionais || 0) + (mes.outras_despesas || 0))],
    ['Despesas tributárias', -mes.despesas_tributarias],
    [
      'Financeiras e depreciação',
      -((mes.despesas_financeiras || 0) + (mes.depreciacao || 0) + (mes.ir_csll || 0)),
    ],
    ['Lucro líquido', mes.lucro_liquido],
  ].map(([rotulo, valor], i) => ({
    rotulo: rotulo as string,
    valor: Math.max((valor as number) || 0, 0),
    cor: CORES_DO_DESTINO[i],
  }));

  const pontos = completo ? resumo.principais : resumo.principais.slice(0, 5);
  const consistentes = resumo.consistencia.filter(c => c.ok === true).length;
  const verificaveis = resumo.consistencia.filter(c => c.ok !== null).length;
  const cz = resumo.cruzamentos_resumo;
  let corDosPontos = '#0F172A';
  if (resumo.contagem.critica) corDosPontos = '#B91C1C';
  else if (resumo.contagem.alta) corDosPontos = '#B45309';

  return (
    <>
      <ParecerDaRevisao resumo={resumo} />

      <div className="kpiGrid">
        <Kpi rotulo="Receita bruta" valor={brlCurto(mes.receita_bruta)}>
          <Variacao atual={atualDaSerie?.receita_bruta} anterior={antDaSerie?.receita_bruta} /> vs.
          mês anterior
        </Kpi>
        <Kpi rotulo="Lucro líquido" valor={brlCurto(mes.lucro_liquido)}>
          margem {pct(ind.margem_liquida_mes?.valor)}
          {completo && ` · ano ${brlCurto(resumo.dre.ano.lucro_liquido)}`}
        </Kpi>
        <Kpi rotulo="Caixa e bancos" valor={brlCurto(resumo.caixa)}>
          <Variacao atual={atualDaSerie?.disponivel} anterior={antDaSerie?.disponivel} /> vs. mês
          anterior
        </Kpi>
        <Kpi
          rotulo="Liquidez corrente"
          valor={
            ind.liquidez_corrente?.valor == null
              ? '—'
              : ind.liquidez_corrente.valor.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
          }
        >
          ativo circulante ÷ passivo circulante
        </Kpi>
        <Kpi
          rotulo="Carga tributária"
          valor={pct(ind.carga_tributaria_mes?.valor)}
          onClick={onCarga}
        >
          {onCarga
            ? `ano ${pct(ind.carga_tributaria_acumulada?.valor)} · clique para abrir os tributos`
            : 'sobre a receita bruta do mês'}
        </Kpi>
        <Kpi
          rotulo="Pontos de atenção"
          valor={`${resumo.contagem.critica} crít. · ${resumo.contagem.alta} altas`}
          cor={corDosPontos}
          tam={17}
        >
          {menores} menores
        </Kpi>
      </div>

      <div style={{ ...CAIXA, gap: 10 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          <div style={TITULO}>Resumo da operação · {mesLongo(competencia)}</div>
          <span style={SUB}>
            Variação contra {mesLongo(compAnt)}. Margens sobre a receita líquida; diferença em
            pontos percentuais (p.p.).
          </span>
        </div>
        <div className="opGrid">
          {operacao.map(o => (
            <div
              key={o.rotulo}
              style={{
                minWidth: 0,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                borderRadius: 12,
                background: '#FFFFFF',
                border: '1px solid #E4EAF1',
              }}
            >
              <span style={{ fontSize: 12, color: '#52525B', fontWeight: 600 }}>{o.rotulo}</span>
              <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.15 }}>{o.valor}</span>
              <span style={{ ...pilula(o.d.fundo, o.d.cor), alignSelf: 'flex-start' }}>
                {o.d.texto}
              </span>
              <span style={{ fontSize: 11, color: '#52525B' }}>{o.sub}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ ...CAIXA, flex: '999 1 520px', padding: '14px 16px 12px', gap: 0 }}>
          <div style={TITULO}>Receita e resultado por mês</div>
          <div style={{ fontSize: 12, color: '#52525B' }}>
            Barras: receita bruta. Linha: lucro líquido.
          </div>
          <div
            role="img"
            aria-label={`Receita bruta e lucro líquido até ${mesLongo(competencia)}`}
            style={{ position: 'relative', height: 200, margin: '14px 0 0 58px' }}
          >
            {[1, 2 / 3, 1 / 3, 0].map(f => (
              <div
                key={f}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${(100 - f * 100).toFixed(1)}%`,
                  borderTop: '1px dashed #DCE3EC',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    right: 'calc(100% + 8px)',
                    top: -7,
                    fontSize: 10.5,
                    color: '#64748B',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {brlCurto(max * f)}
                </span>
              </div>
            ))}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'flex-end',
                gap: '4%',
                padding: '0 2%',
              }}
            >
              {serie.map((s, i) => (
                <div
                  key={s.competencia}
                  title={`${mesLongo(s.competencia)}: receita ${brl(receitas[i])} · lucro ${brl(lucros[i])}`}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: `${((receitas[i] / max) * 100).toFixed(1)}%`,
                    borderRadius: '4px 4px 0 0',
                    background: s.competencia === competencia ? '#1B6FD0' : '#93C5FD',
                  }}
                />
              ))}
            </div>
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                overflow: 'visible',
              }}
            >
              <polyline
                points={lucros.map((v, i) => `${cx(i).toFixed(2)},${yL(v).toFixed(2)}`).join(' ')}
                fill="none"
                stroke="#0F172A"
                strokeWidth="2"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            {lucros.map((v, i) => (
              <span
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                style={{
                  position: 'absolute',
                  left: `${cx(i).toFixed(2)}%`,
                  top: `${yL(v).toFixed(2)}%`,
                  width: 8,
                  height: 8,
                  margin: '-4px 0 0 -4px',
                  boxSizing: 'border-box',
                  borderRadius: '50%',
                  background: '#FFFFFF',
                  border: '1.6px solid #0F172A',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '4%', padding: '0 2%', margin: '6px 0 0 58px' }}>
            {serie.map(s => (
              <span
                key={s.competencia}
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'center',
                  fontSize: 10.5,
                  color: '#64748B',
                }}
              >
                {mesCurto(s.competencia)}
              </span>
            ))}
          </div>
        </div>
        <div style={{ ...CAIXA, flex: '1 1 300px', gap: 10 }}>
          <div>
            <div style={TITULO}>Para onde foi a receita</div>
            <div style={{ fontSize: 12, color: '#52525B' }}>
              Como a receita bruta do mês se dividiu entre impostos, custos, despesas e lucro.
            </div>
          </div>
          {receita <= 0 ? (
            <span style={{ fontSize: 12.5, color: '#52525B' }}>Sem receita no mês.</span>
          ) : (
            destino.map(d => (
              <div key={d.rotulo} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    fontSize: 12.5,
                  }}
                >
                  <span>{d.rotulo}</span>
                  <span style={{ color: '#52525B', whiteSpace: 'nowrap' }}>
                    {brlCurto(d.valor)} · {pct(d.valor / receita, 0)}
                  </span>
                </div>
                <span
                  style={{ display: 'block', height: 5, borderRadius: 3, background: '#EEF2F6' }}
                >
                  <span
                    style={{
                      display: 'block',
                      height: 5,
                      borderRadius: 3,
                      width: `${Math.min(100, (d.valor / receita) * 100)}%`,
                      background: d.cor,
                    }}
                  />
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {completo && (
        <div className="cz3">
          <Anel
            titulo="Cobertura da revisão"
            sub="Quantos dos 11 cruzamentos do procedimento puderam ser feitos com os dados disponíveis."
            valor={cz.feitos}
            total={11}
            cor="#1B6FD0"
            rotulo="cruzamentos com dados"
          />
          <Anel
            titulo="Cruzamentos que conferem"
            sub="Comparações numéricas sem diferença entre as fontes."
            valor={cz.conferem}
            total={cz.comparaveis}
            cor={cz.conferem === cz.comparaveis ? '#16A34A' : '#D97706'}
            rotulo="comparações sem diferença"
          />
          <Anel
            titulo="Consistência das peças"
            sub="Balancete, balanço, DRE e fluxo de caixa fecham entre si."
            valor={consistentes}
            total={verificaveis}
            cor="#16A34A"
            rotulo="verificações de consistência"
          />
        </div>
      )}

      <div
        className={completo ? 'cz2' : undefined}
        style={completo ? undefined : { display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ ...CAIXA, gap: 4 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 10,
              marginBottom: 6,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={TITULO}>Principais pontos</div>
              <span style={SUB}>O que precisa ser resolvido antes de liberar o fechamento.</span>
            </div>
            <button
              type="button"
              onClick={onVerTodas}
              style={{
                flexShrink: 0,
                padding: '4px 12px',
                border: '1px solid #D4DAE2',
                borderRadius: 8,
                background: '#FFFFFF',
                fontSize: 12,
                fontWeight: 600,
                color: '#0F172A',
                cursor: 'pointer',
              }}
            >
              Ver todas
            </button>
          </div>
          {pontos.length === 0 && (
            <span
              style={{
                padding: '10px 0',
                borderTop: '1px solid rgba(15, 23, 42, .08)',
                fontSize: 12.5,
                color: '#52525B',
              }}
            >
              Nenhum ponto crítico ou alto nesta competência.
            </span>
          )}
          {pontos.map(p => {
            const sev = SEVERIDADE[p.severidade];
            return (
              <div
                key={`${p.codigo}|${p.detalhe}`}
                style={{
                  display: 'flex',
                  gap: 12,
                  padding: '9px 0',
                  borderTop: '1px solid rgba(15, 23, 42, .08)',
                }}
              >
                <span style={{ ...pilula(sev.suave, sev.cor), alignSelf: 'flex-start' }}>
                  {sev.rotulo}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>
                    {p.codigo} · {p.titulo}
                  </span>
                  <span style={{ fontSize: 11.5, color: '#52525B' }}>{p.detalhe}</span>
                </div>
              </div>
            );
          })}
        </div>
        {completo && (
          <section aria-label="Limitações desta revisão" style={{ ...CAIXA, gap: 8 }}>
            <div style={TITULO}>Limitações desta revisão</div>
            <span style={SUB}>O que não pôde ser verificado com os dados disponíveis.</span>
            {resumo.limitacoes.length === 0 ? (
              <span style={{ fontSize: 12.5, color: '#52525B' }}>
                Nenhuma limitação registrada.
              </span>
            ) : (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  fontSize: 12.5,
                }}
              >
                {resumo.limitacoes.map(l => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </>
  );
}
